import {Component, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ReactiveFormsModule} from '@angular/forms';
import {dayNumOfWeek, isBlank, isNotBlank} from '../../../../../_utils/lang';
import {Observable} from 'rxjs';
import {UserTimeRestriction} from '../../../../../_data/admin/user';
import {NgClass} from '@angular/common';
import {ValidationErrorsComponent} from '../../../../../common/forms/validation-errors/validation-errors.component';
import {TranslateModule} from '@ngx-translate/core';

/**
 * This component represents a single row (for a given day of the week) in the create-user form for setting up access times.
 */
@Component({
    selector: 'app-day-access-time',
    templateUrl: './day-access-time.component.html',
    styleUrls: ['./day-access-time.component.scss'],
    imports: [ReactiveFormsModule, NgClass, ValidationErrorsComponent, TranslateModule]
})
export class DayAccessTimeComponent implements OnInit {

  /** The FormGroup that will have sub groups for each day of the week */
  @Input() allowedTimesFormGroup: UntypedFormGroup;

  /** The short code for the day of week. Eg: mon, tue */
  @Input() dayShort: string;

  /** The full, translated, day of the week */
  @Input() dayFull: string;

  /** @see {@link AccessTimesComponent#userFormSubmitted$} */
  @Input() userFormSubmitted$: Observable<boolean>;

  /**
   * Indicates that the user clicked "Apply to All" and the parent component needs to update the sibling components.
   * The emitted object contains the values that need to be applied to the other components.
   */
  @Output('applyTimeToAll') _applyTimeToAll = new EventEmitter<UserTimeRestriction>();

  dayFormGroup: UntypedFormGroup;
  userFormSubmitted: boolean;
  forceDisplayInvalidFeedback = false;

  private readonly formBuilder = inject(UntypedFormBuilder);

  /**
   * Validates the following:
   *  - If set to "allowed time": there is both a start and end time, and the end time is after the start
   */
  private static allowedTimeValidator() {
    return (group: UntypedFormGroup): ValidationErrors => {
      const allowed = group.controls.allowed.value;
      const start = group.controls.startTime.value;
      const end = group.controls.endTime.value;

      group.controls.startTime.setErrors(null);
      group.controls.endTime.setErrors(null);

      //We only need to really validate anything if the radio is set to "allowed time"
      if(allowed === 'y') {
        //Make sure both times are filled in
        if(isBlank(start)) {
          group.controls.startTime.setErrors({required: true});
        }
        if(isBlank(end)) {
          group.controls.endTime.setErrors({required: true});
        }

        if(isNotBlank(start) && isNotBlank(end)) {
          //Verify the end time is after the start time
          const startSplit = start.split(':');
          const endSplit = end.split(':');

          //Convert the two entries of the split array into minutes, for a simple compare.  First entry is hours, so x60, plus the minutes.
          const startMinutes = (parseInt(startSplit[0], 10) * 60) + parseInt(startSplit[1], 10);
          const endMinutes = (parseInt(endSplit[0], 10) * 60) + parseInt(endSplit[1], 10);
          if(endMinutes < startMinutes) {
            group.controls.endTime.setErrors({startEndTime: true});
          }
        }

      }
      //don't return an error object because the error would be on the whole section.
      //we've already applied any valid errors to the appropriate control.
      return null;
    };
  }

  /** Init lifecycle */
  ngOnInit(): void {
    //Modify the access times FormGroup, adding a sub group for the day's entry
    this.dayFormGroup = this.formBuilder.group(
      {
        allowed:   'n',
        startTime: new UntypedFormControl({value: {}, disabled: true}),
        endTime:   new UntypedFormControl({value: {}, disabled: true})
      },
      {validators: DayAccessTimeComponent.allowedTimeValidator()}
    );
    this.allowedTimesFormGroup.setControl(this.dayShort, this.dayFormGroup);
    this.setAllowedTimes();
    this.dayFormGroup.controls.allowed.setValue('y');

    //Tie the userFormSubmitted$ field to the Subject state
    this.userFormSubmitted$.subscribe(v => this.userFormSubmitted = v);
  }

  /** When the radio is changed from "allowed times" to anything else, disable the time pickers */
  disableAllowedTimes() {
    this.dayFormGroup.controls.startTime.disable();
    this.dayFormGroup.controls.startTime.setValue('');
    this.dayFormGroup.controls.endTime.disable();
    this.dayFormGroup.controls.endTime.setValue('');
  }

  /**
   * Used in two different scenarios:
   *  - On load of an existing user: set the times they already have configured
   *  - When choosing "allowed time" and a default start/end needs to be set (for weekdays)
   */
  setAllowedTimes(startTime?, endTime?) {
    const startControl = this.dayFormGroup.controls.startTime;
    const endControl = this.dayFormGroup.controls.endTime;

    //Any days gets defaulted to 8 - 20
    startTime = startTime ?? [startControl.value, '08:00'].find(v => typeof (v) === 'string' && isNotBlank(v));
    endTime = endTime ?? [endControl.value, '20:00'].find(v => typeof (v) === 'string' && isNotBlank(v));
    startControl.enable();
    startControl.setValue(startTime);
    endControl.enable();
    endControl.setValue(endTime);
  }

  /**
   * When the apply-to-all button is clicked, we need to emit that up to the parent component, which will update the sibling components with the same time
   *
   * @see {@link update}
   * @see {@link AccessTimesComponent#applyTimeToAll}
   */
  applyTimeToAll() {
    const allowedValue = this.dayFormGroup.controls.allowed.value;
    const startTimeValue = this.dayFormGroup.controls.startTime.value;
    const endTimeValue = this.dayFormGroup.controls.endTime.value;

    //Check if the form is valid before we bother applying it to all the others.
    if(this.dayFormGroup.valid) {
      this._applyTimeToAll.emit({
        dayOfWeek: this.dayShort,
        allDay:    allowedValue === 'all-day' ? 1 : (allowedValue === 'n' ? -1 : 0),
        timeFrom:  startTimeValue,
        timeTo:    endTimeValue
      });
    } else {
      //The form has to be properly filled out when the user wants to apply the time to all other rows.
      // So we'll show the errors if they haven't done that.
      this.forceDisplayInvalidFeedback = true; //show errors
      console.debug('Can\'t apply to other rows.  There\'s form errors');
    }
  }

  /**
   * Updates the input elements based on the provided UserTimeRestriction.
   * Used for example when the parent component wants to update all of the sibling components to the same thing (when a user has clicked "apply to all")
   *
   * @see {@link applyTimeToAll}
   * @see {@link AccessTimesComponent#applyTimeToAll}
   */
  update(userTimeRestriction: UserTimeRestriction) {
    if(userTimeRestriction.allDay === 1) {
      this.dayFormGroup.controls.allowed.setValue('all-day');
      this.disableAllowedTimes();
    } else if(userTimeRestriction.allDay === -1) {
      this.dayFormGroup.controls.allowed.setValue('n');
      this.disableAllowedTimes();
    } else {
      this.setAllowedTimes(userTimeRestriction.timeFrom, userTimeRestriction.timeTo);
      //Do this last so the validator doesn't cause problems.
      //Otherwise the validator has to be smarter about handling empty objects instead of strings
      this.dayFormGroup.controls.allowed.setValue('y');
    }
  }

  /**
   * Given a list of UserTimeRestriction objects, find the one for the right day and load the form from it.
   *
   * @see {@link AccessTimesComponent#applyTimes}
   */
  updateFrom(userTimeRestrictions: UserTimeRestriction[]) {
    const dayNum = dayNumOfWeek(this.dayShort);
    //First, find the time restriction that's for the right day
    const rightDaysTimeRestriction = userTimeRestrictions.find(v => v?.dayOfWeek === dayNum);
    //Then load the form fields from that entry
    if(rightDaysTimeRestriction) {
      this.update(rightDaysTimeRestriction);
    } else {
      console.warn(`User with bad data?  Can't find a day of the week that corresponds to 1-based index ${dayNum}`);
    }
  }
}
